import useGlobalFlag from 'hooks/useGlobalFlag'
import { RbacActions, RbacSubjects } from 'enrollment-platform/rbac/constants'
import { usePermission } from 'enrollment-platform/rbac/usePermission'
import { US_RESTRICTED_DATA_ENFORCEMENT_FLAG_KEY } from 'enrollment-platform/rbac/us-restricted-data-enforcement-flag'
import { useUsRestrictedDataAccess } from 'enrollment-platform/rbac/useUsRestrictedDataAccess'

/**
 * Whether the current user may download the Funding Invoice list's Excel files.
 * Mirrors EPS `GET /funding-invoice/invoice-deliveries/:id/excel`, which needs
 * Download on FundingInvoice and, since the files carry employee-level PHI
 * (EMS-10939), Download on UsRestrictedData. The page itself only needs Read
 * on FundingInvoice. `Manage` is the action wildcard.
 *
 * UI ergonomics only — the server is the real boundary. The UsRestrictedData
 * half applies only while the EPS GLOBAL flag
 * `service.eps.us-restricted-data.enable-enforcement` is on, matching
 * `UsRestrictedGuard`, which is a no-op while it is off. False while the flag
 * or either ability is loading, so the column never flashes in for a user who
 * turns out not to have it.
 */
export function useCanDownloadFromFundingInvoice(): boolean {
  const fundingInvoice = usePermission(
    RbacActions.Download,
    RbacSubjects.FundingInvoice
  )
  const { isEnabled: isUsDataEnforced, isLoading: isFlagLoading } =
    useGlobalFlag(US_RESTRICTED_DATA_ENFORCEMENT_FLAG_KEY)
  const { hasUsRestrictedDataAccess } = useUsRestrictedDataAccess(
    RbacActions.Download
  )
  if (isFlagLoading || fundingInvoice.isLoading) return false
  return (
    fundingInvoice.allowed && (!isUsDataEnforced || hasUsRestrictedDataAccess)
  )
}
